// Port of ios/Sources/ViewModels/MentionComposer.swift.
//
// The pure half of @mention autocomplete: detecting whether the cursor is inside a mention
// being typed, filtering candidates, and applying a chosen one. Ported from Android with its
// test table.
//
// OFFSETS: the Swift file counts Characters (grapheme clusters); this port counts UTF-16 code
// units, which is what a `<textarea>`'s `selectionStart` speaks and what the Android original
// (Kotlin `String` indices) counts too. The two only differ inside non-BMP text (emoji), and every
// character the trigger rules look at (`@`, ASCII name characters, whitespace) is a single unit.
import type { UserRecord } from './records';

/**
 * An "@" the user is typing at: `start` is the index of the "@" itself, `query` the
 * partial name after it (empty — a bare "@" — opens the full list).
 */
export interface ActiveMention {
  start: number;
  query: string;
}

export interface Candidate {
  userId: string;
  username: string;
  displayName: string;
  /**
   * Channel participants sort before merely-known users: the person you are talking TO
   * is almost always the person you mean.
   */
  isParticipant: boolean;
}

/**
 * `@here`/`@channel`/`@all` — server-side broadcast keywords, not users, but they complete
 * and render like mentions everywhere in Mattermost.
 */
export const specialMentions: readonly string[] = ['here', 'channel', 'all'];

function isMentionChar(c: string): boolean {
  return /^[A-Za-z0-9._-]$/.test(c);
}

function isWhitespace(c: string): boolean {
  return /^\s$/u.test(c);
}

/**
 * The mention being typed at `cursor`, or null. An "@" only triggers at the start of the
 * text or after whitespace — `correo@empresa.com` must never open the popup — and the
 * cursor must sit inside or at the end of the token, so clicking back into earlier text
 * does not resurrect a popup for a mention that is already complete.
 */
export function activeMention(text: string, cursor: number): ActiveMention | null {
  if (cursor < 1 || cursor > text.length) return null;

  let index = cursor - 1;
  while (index >= 0 && isMentionChar(text[index])) {
    index -= 1;
  }
  if (index < 0 || text[index] !== '@') return null;
  if (index > 0 && !isWhitespace(text[index - 1])) return null;

  return { start: index, query: text.slice(index + 1, cursor) };
}

function compareLowered(a: string, b: string): number {
  // Swift's `<` on Strings: Unicode scalar order, not locale collation.
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
}

/**
 * Participants first, then alphabetically within each group, capped. An empty query lists
 * everyone — a bare "@" is a legitimate "who is here?". Matching is CONTAINS, not prefix:
 * people remember "torres", not that it starts with "ana.".
 */
export function filterCandidates(candidates: readonly Candidate[], query: string, limit = 6): Candidate[] {
  const needle = query.toLowerCase();
  const seen = new Set<string>();
  return candidates
    .filter(
      (candidate) =>
        needle === '' ||
        candidate.username.toLowerCase().includes(needle) ||
        candidate.displayName.toLowerCase().includes(needle),
    )
    .filter((candidate) => {
      if (seen.has(candidate.username)) return false;
      seen.add(candidate.username);
      return true;
    })
    .sort((a, b) => {
      if (a.isParticipant !== b.isParticipant) return a.isParticipant ? -1 : 1;
      return compareLowered(a.displayName, b.displayName);
    })
    .slice(0, limit);
}

/**
 * The strip's FIRST tier: everyone in the channel — the server's roster for this query plus
 * whoever authored the loaded posts — all ranked as members (user, 2026-10-09: "inside threads,
 * if the user hasn't participated it is not listing him… in the channel the thread inherits
 * he is present, so it must show him"; "always show the current channel on top").
 */
export function memberTier(local: readonly Candidate[], roster: readonly Candidate[]): Candidate[] {
  return [...roster, ...local].map((c) => ({
    userId: c.userId,
    username: c.username,
    displayName: c.displayName,
    isParticipant: true,
  }));
}

/**
 * `displayName` is `ChannelListDerivation.userDisplayName` on the Apple side (which defers to
 * `TeammateNameDisplay`); it is injected here because that module belongs to another port.
 */
export function candidate(user: UserRecord, displayName: (user: UserRecord) => string): Candidate {
  return { userId: user.id, username: user.username, displayName: displayName(user), isParticipant: false };
}

/**
 * Team members matching `query` who are NOT already offered by `known` — the "not in
 * this conversation" tier (user order 2026-08-31): mentioning someone pulls them into the
 * thread even though they never joined the channel. Excluded by USERNAME against the
 * whole known roster, not just its query-filtered subset, so a person can never appear in
 * both sections at once.
 */
export function filterExternalCandidates(
  known: readonly Candidate[],
  fetched: readonly Candidate[],
  query: string,
  limit = 6,
): Candidate[] {
  const knownUsernames = new Set(known.map((k) => k.username.toLowerCase()));
  const needle = query.toLowerCase();
  const seen = new Set<string>();
  return fetched
    .filter((c) => !knownUsernames.has(c.username.toLowerCase()))
    .filter(
      (c) => needle === '' || c.username.toLowerCase().includes(needle) || c.displayName.toLowerCase().includes(needle),
    )
    .filter((c) => {
      const key = c.username.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => compareLowered(a.displayName, b.displayName))
    .slice(0, limit);
}

/** Special keywords matching `query`, offered after real people. */
export function matchingSpecials(query: string): string[] {
  const needle = query.toLowerCase();
  return specialMentions.filter((s) => needle === '' || s.startsWith(needle));
}

export interface Applied {
  text: string;
  cursor: number;
}

/**
 * Replaces the active token with `@username ` (trailing space included — the user is
 * mid-sentence and making them type it is petty), cursor after it.
 */
export function apply(text: string, cursor: number, active: ActiveMention, username: string): Applied {
  const replacement = '@' + username + ' ';
  const newText = text.slice(0, active.start) + replacement + text.slice(cursor);
  return { text: newText, cursor: active.start + replacement.length };
}
